/**
 * The AI reply suggestion's language (cs-analysis docs/design-client-comms.md §8.2 "The draft's language", T006,
 * 2026-10-27). A representative saw Hindi drafts on English conversations: the drafter was told to "mirror the
 * client's language" and guessed. The api now decides the language before the model is called — the channel's
 * remembered pick, else the client's own messages (English unless they write Hindi, Hinglish or another language)
 * — and says which on every answer:
 *   language        english | hindi | hinglish | client — what the draft is written in (client = the same language
 *                   the client writes in: Session 0's diff check, 2026-10-27, a global company)
 *   languageBasis   chosen | devanagari | other_script | hindi_words | other_latin | english_words | default — why
 *   languageDetail  for client: what the api saw in the client's messages ("Arabic script", "Latin letters, not
 *                   English or Hindi"); never a language guessed
 *   languageChoice  auto | english | hindi | hinglish | client — the channel's remembered pick (auto = none)
 * The card's switch sends POST comms/channels/:id/suggest-reply { force: true, lang }: the pick is remembered for the
 * channel (every representative's next drafts in it, the rep chat app's too) and the draft is written again in it.
 * Pure helpers; CommsThread.jsx renders the switch; reply_language.test.js.
 */

/** The switch's options, in its order; values as the api takes them (cs-analysis REPLY_LANGUAGE_CHOICES). */
export const REPLY_LANGUAGE_CHOICES = ['auto', 'english', 'hindi', 'hinglish', 'client'];
export const REPLY_LANGUAGE_LABELS = { auto: 'Auto', english: 'English', hindi: 'Hindi', hinglish: 'Hinglish', client: 'Same as the client' };
const LANGUAGES = ['english', 'hindi', 'hinglish', 'client'];
/** The bases the api reads from the client's own messages; only these may say so on the card. */
const CLIENT_TEXT_BASES = ['devanagari', 'other_script', 'hindi_words', 'other_latin', 'english_words'];

export const REPLY_LANGUAGE_SWITCH_TITLE = "The draft's language for this chat — remembered for every representative's next drafts here. Auto follows the client's own messages.";

/** The pick as the api answered it; an older api (no field) or any other value is auto. */
export const language_choice_of = (r) => (r && REPLY_LANGUAGE_CHOICES.includes(r.languageChoice) ? r.languageChoice : 'auto');

/** The response's language fields kept on the thread's suggestion state (suggestion_of); nulls for an older api. */
export const language_of = (r) => ({
    language: r && LANGUAGES.includes(r.language) ? r.language : null,
    languageBasis: r && typeof r.languageBasis === 'string' ? r.languageBasis : null,
    languageDetail: r && typeof r.languageDetail === 'string' && r.languageDetail ? r.languageDetail : null,
    languageChoice: language_choice_of(r),
});

/**
 * The card's words for the draft's language: which, and why (a pick for this chat, or auto from the client's messages).
 * A basis the card does not know (a newer api, none sent) names the language and claims no source.
 */
export const language_line = (s) => {
    if (!s || !LANGUAGES.includes(s.language)) return '';
    const name = s.language === 'client'
        ? `the client's language${s.languageDetail ? ` (${s.languageDetail})` : ''}`
        : REPLY_LANGUAGE_LABELS[s.language];
    if (s.languageChoice && s.languageChoice !== 'auto') return `Written in ${name} · picked for this chat`;
    if (s.languageBasis === 'default') return `Written in ${name} · auto (no client text to go by)`;
    if (CLIENT_TEXT_BASES.includes(s.languageBasis)) return `Written in ${name} · auto, from the client's messages`;
    return `Written in ${name}`;
};

/** The body the switch posts: a tap's draft (past the recency guard, low confidence shown) in the language picked. */
export const switch_body = (choice) => ({ force: true, lang: REPLY_LANGUAGE_CHOICES.includes(choice) ? choice : 'auto' });
